import { useEffect, useRef } from 'react';
import type { IntakeFormKind } from '@leanin/shared/intake';
import { trackIntakeFormViewed } from '@/lib/analytics/events';
import type { ApplicantIntakeForm } from './intake-answer-model';

// Fires `intake_form_viewed` once per form version while the form is visible.
// Shared by both applicant surfaces (the member join modal A/B and the
// Circle→Network application C). The fire-once-per-version dedupe is the
// reason this is a hook and not two inline effects: a React re-render must
// not re-count a view, but a genuinely new version — the leader edited the
// form mid-view, the applicant refetched a stale version — is a new view and
// must re-count. Closing the surface resets the guard so reopening re-counts.
export function useTrackIntakeFormViewed(
  form: ApplicantIntakeForm | null,
  formKind: IntakeFormKind,
  active: boolean,
): void {
  const firedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!active) {
      firedFor.current = null;
      return;
    }
    if (form === null || firedFor.current === form.formVersionId) return;
    firedFor.current = form.formVersionId;
    trackIntakeFormViewed({
      form_kind: formKind,
      form_version: form.versionNumber,
    });
  }, [active, form, formKind]);
}
